import { isTaskLevelSchedule } from '@dadohr/shared/domainTypes/schedules';
import { FS } from '@dadohr/shared/versions';
import { Card } from '@material-ui/core';
import { keyBy } from 'lodash';
import { CanvasBar } from '../../../layout/Canvas';
import { Page } from '../../../layout/Page';
import { Section } from '../../../layout/Section';
import { batchUpdateDocs, store } from '../../../services/firebase';
import { callFirebaseFunction } from '../../../services/firebaseFunctions';
import { toScheduleDoc } from '../../../services/schedules/documents';
import { ONE_DAY } from '../../../services/time';
import { ButtonContainer } from '../../components/ButtonContainer';
import { SafeExecuteButton } from '../../components/SafeExecuteButton';
import {
  AnchorLinks,
  AnchoredSections,
  ISection
} from '../../layout/AnchoredSection';
import {
  getAllExperienceTemplates,
  getAllExperiences
} from '../../services/experiences';
import { getAllOrgIds, getAllOrgs } from '../../services/orgs';
import { publishTopic } from '../../services/pubsub';

const Orgs = () => {
  return (
    <Section>
      <CanvasBar>Orgs</CanvasBar>
      <Card>
        <ButtonContainer>
          <SafeExecuteButton
            variant="contained"
            color="primary"
            onClick={async (execute) => {
              const orgs = await getAllOrgs();
              const toUpdate = orgs.filter((o) => !o.data.tz);
              console.log('with missing tz', toUpdate);
              if (execute) {
                await batchUpdateDocs(toUpdate, (o) => ({
                  tz: 'Europe/Berlin'
                }));
              }
            }}
          >
            Add missing timezones
          </SafeExecuteButton>
        </ButtonContainer>
      </Card>
    </Section>
  );
};

const Events = () => {
  return (
    <Section>
      <CanvasBar>Experience Templates</CanvasBar>
      <Card>
        <ButtonContainer>
          <SafeExecuteButton
            variant="contained"
            color="secondary"
            onClick={async (execute) =>
              callFirebaseFunction('events-seedExperienceEvents', { execute })
            }
          >
            Seed experience events
          </SafeExecuteButton>
        </ButtonContainer>
      </Card>
    </Section>
  );
};

const ExperienceTemplates = () => {
  return (
    <Section>
      <CanvasBar>Experience Templates</CanvasBar>
      <Card>
        <ButtonContainer>
          <SafeExecuteButton
            variant="contained"
            color="secondary"
            onClick={async (execute) => {
              const tpls = await getAllExperienceTemplates();
              const nextTpls = tpls.map((tpl) => ({
                ...tpl,
                data: {
                  ...tpl.data,
                  tasks: tpl.data.tasks.map((t) => ({
                    ...t,
                    end: t.end + ONE_DAY
                  }))
                }
              }));

              console.log(nextTpls);

              if (execute) {
                await batchUpdateDocs(nextTpls, (d) => ({ tasks: d.tasks }));
              }
            }}
          >
            Fix task durations
          </SafeExecuteButton>
        </ButtonContainer>
      </Card>
    </Section>
  );
};

const ExperienceInstances = () => {
  return (
    <Section>
      <CanvasBar>Experience Instances</CanvasBar>
      <Card>
        <ButtonContainer>
          <SafeExecuteButton
            variant="contained"
            color="primary"
            onClick={(execute) =>
              callFirebaseFunction(
                'experiences-_syncAllDenormalizedExperienceStatuses',
                { execute }
              )
            }
          >
            Denormalize all statuses
          </SafeExecuteButton>

          <SafeExecuteButton
            variant="contained"
            color="primary"
            onClick={(execute) =>
              callFirebaseFunction('experiences-touchAllInstances', {
                execute
              }).then((r) => console.log(r))
            }
          >
            Touch all instances
          </SafeExecuteButton>

          <SafeExecuteButton
            variant="contained"
            color="primary"
            onClick={async (execute) => {
              const orgIds = await getAllOrgIds();
              console.log(orgIds);
              await Promise.all(
                orgIds.map(
                  (orgId) =>
                    execute &&
                    publishTopic({
                      topic: 'exp-experiences-checkForLatenessInOrg',
                      payload: { ts: Date.now(), orgId }
                    })
                )
              );
            }}
          >
            Check lateness on everything
          </SafeExecuteButton>
        </ButtonContainer>
      </Card>
    </Section>
  );
};

const addTemplateIdToSchedules = async (execute: boolean) => {
  const exps = await getAllExperiences();
  const expsById = keyBy(exps, (e) => e.id);
  const docs = await store()
    .collection(FS.schedules)
    .get()
    .then((s) => s.docs.map(toScheduleDoc))
    .then((ds) => ds.filter((d) => !d.data.templateId));

  docs.forEach((d) => {
    const exp = expsById[d.data.experienceId];
    if (!exp) {
      return;
    }
    d.data.templateId = exp.data.templateId;
  });
  const toUpdate = docs.filter((d) => !!d.data.templateId);
  console.log(`${toUpdate.length} of ${docs.length}`, toUpdate);
  if (execute) {
    await batchUpdateDocs(toUpdate, (d) => ({ templateId: d.templateId }));
  }
};

const addTaskTemplateIdToSchedules = async (execute: boolean) => {
  const exps = await getAllExperiences();
  const expsById = keyBy(exps, (e) => e.id);
  const docs = await store()
    .collection(FS.schedules)
    .get()
    .then((s) => s.docs.map(toScheduleDoc))
    .then((ds) => ds.filter(isTaskLevelSchedule))
    .then((ds) => ds.filter((d) => !d.data.taskTemplateId));

  docs.forEach((d) => {
    const exp = expsById[d.data.experienceId];
    if (!exp) {
      return;
    }
    const task = exp.data.tasks.find((t) => d.data.taskId === t.id);
    if (!task) {
      return;
    }
    d.data.taskTemplateId = task.templateId;
  });
  const toUpdate = docs.filter((d) => !!d.data.taskTemplateId);
  console.log(`${toUpdate.length} of ${docs.length}`, toUpdate);
  if (execute) {
    await batchUpdateDocs(toUpdate, (d) => ({
      taskTemplateId: d.taskTemplateId
    }));
  }
};

const Schedules = () => {
  return (
    <Section>
      <CanvasBar>Schedules</CanvasBar>
      <Card>
        <ButtonContainer>
          <SafeExecuteButton
            variant="contained"
            color="secondary"
            onClick={(execute) => addTemplateIdToSchedules(execute)}
          >
            Add template id
          </SafeExecuteButton>

          <SafeExecuteButton
            variant="contained"
            color="secondary"
            onClick={(execute) => addTaskTemplateIdToSchedules(execute)}
          >
            Add task template id
          </SafeExecuteButton>
        </ButtonContainer>
      </Card>
    </Section>
  );
};

export const PageMigrations = () => {
  const SECTIONS: ISection[] = [
    {
      anchor: 'orgs',
      label: 'Orgs',
      el: <Orgs />
    },
    {
      anchor: 'events',
      label: 'Events',
      el: <Events />
    },
    {
      anchor: 'exp-tpls',
      label: 'Experience Templates',
      el: <ExperienceTemplates />
    },
    {
      anchor: 'exp-inst',
      label: 'Experience Instances',
      el: <ExperienceInstances />
    },
    {
      anchor: 'schedules',
      label: 'Schedules',
      el: <Schedules />
    }
  ];
  return (
    <>
      <Page>
        <AnchorLinks sections={SECTIONS} offset={100} />
        <AnchoredSections sections={SECTIONS} />
      </Page>
    </>
  );
};
